import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight, Sparkles } from "lucide-react";
import { useAgency } from "../../contexts/AgencyContext";
import { useAuth } from "../../contexts/AuthContext";
import { getWorkspacesForRole, isCaregiverRole } from "../../config/workspaces";

/**
 * Leynix Sidebar — Workflow-First Navigation
 * 
 * Design Principles:
 * 1. Role-based: Navigation adapts to user's role
 * 2. Workspace-focused: High-level workspaces, not modules
 * 3. Contextual: Sub-items appear within workspaces
 * 4. 10-Second Rule: User understands where they are immediately
 */

export default function Sidebar({ onNavigate }) {
  const { agency } = useAgency();
  const { user } = useAuth();
  const location = useLocation();
  const [expandedWorkspace, setExpandedWorkspace] = useState(null);
  
  // Get workspaces based on user role
  const workspaces = getWorkspacesForRole(user?.role);
  const isCaregiver = isCaregiverRole(user?.role);
  
  // Truncate long organization names
  const agencyName = agency?.name || "Leynix";
  const truncatedName = agencyName.length > 22 ? agencyName.slice(0, 20) + "…" : agencyName;

  // Determine which workspace is active based on current path
  const getActiveWorkspace = () => {
    const path = location.pathname;
    for (const workspace of workspaces) {
      if (path.startsWith(workspace.path)) {
        return workspace.id;
      }
    }
    return 'home';
  };

  const activeWorkspaceId = getActiveWorkspace();

  // Toggle workspace expansion
  const toggleWorkspace = (workspaceId) => {
    setExpandedWorkspace(expandedWorkspace === workspaceId ? null : workspaceId);
  };

  return (
    <aside className="w-[260px] shrink-0 bg-[#FBFBFA] border-r border-[#E2E8F0] flex flex-col h-full">
      {/* Brand Header */}
      <div className="px-5 py-5 border-b border-[#E2E8F0]">
        <div className="flex items-center gap-3" data-testid="sidebar-brand">
          <div
            className="w-9 h-9 rounded flex items-center justify-center font-bold text-sm shrink-0"
            style={{ backgroundColor: "#0D1B33", color: "#176DFF" }}
          >
            L
          </div>
          <div className="min-w-0 flex-1">
            <div 
              className="text-sm font-semibold tracking-tight truncate text-[#0D1B33]" 
              title={agencyName}
              data-testid="sidebar-agency-name"
            >
              {truncatedName}
            </div>
            <div className="text-[11px] text-[#64748B]">
              {isCaregiver ? 'My Workspace' : 'Operations Platform'}
            </div>
          </div>
        </div>
      </div>

      {/* Workspace Navigation */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin px-3 py-4">
        <div className="space-y-1">
          {workspaces.map((workspace) => {
            const Icon = workspace.icon;
            const isActive = activeWorkspaceId === workspace.id;
            const isExpanded = expandedWorkspace === workspace.id;
            const hasSubItems = workspace.subItems && workspace.subItems.length > 0;
            
            // Special handling for Ask Leynix - it's a global action
            if (workspace.id === 'ask-leynix') {
              return (
                <button
                  key={workspace.id}
                  onClick={() => {
                    // Trigger Ask Leynix palette (⌘K)
                    const event = new KeyboardEvent('keydown', {
                      key: 'k',
                      metaKey: true,
                      bubbles: true
                    });
                    document.dispatchEvent(event);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all duration-150 bg-gradient-to-r from-[#176DFF]/10 to-[#29C0FF]/10 hover:from-[#176DFF]/20 hover:to-[#29C0FF]/20 border border-[#176DFF]/20"
                  data-testid="nav-ask-leynix"
                >
                  <Sparkles className="w-4 h-4 text-[#176DFF]" />
                  <span className="text-sm font-medium text-[#176DFF]">{workspace.name}</span>
                  <span className="ml-auto text-[10px] text-[#64748B] bg-white/60 px-1.5 py-0.5 rounded">⌘K</span>
                </button>
              );
            }

            // Special handling for CareHub - highlighted for pilot
            if (workspace.id === 'carehub') {
              return (
                <div key={workspace.id} className="mb-2">
                  <NavLink
                    to={workspace.path}
                    onClick={onNavigate}
                    className={({ isActive: linkActive }) => `
                      flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-150 border-2
                      ${linkActive || isActive
                        ? 'bg-emerald-600 text-white border-emerald-600' 
                        : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300'
                      }
                    `}
                    data-testid="nav-carehub"
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      isActive ? 'bg-white/20' : 'bg-emerald-100'
                    }`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <span className="text-sm font-semibold block">{workspace.name}</span>
                      <span className={`text-[10px] ${isActive ? 'text-white/70' : 'text-emerald-600'}`}>
                        Add caregivers & clients
                      </span>
                    </div>
                  </NavLink>
                </div>
              );
            }
            
            return (
              <div key={workspace.id}>
                {/* Main workspace item */}
                {hasSubItems ? (
                  <button
                    onClick={() => toggleWorkspace(workspace.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all duration-150 ${
                      isActive 
                        ? 'bg-[#0D1B33] text-white' 
                        : 'text-[#334155] hover:bg-[#F1F5F9]'
                    }`}
                    data-testid={`nav-${workspace.id}`}
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="text-sm font-medium flex-1">{workspace.name}</span>
                    {hasSubItems && (
                      isExpanded 
                        ? <ChevronDown className="w-4 h-4 opacity-50" />
                        : <ChevronRight className="w-4 h-4 opacity-50" />
                    )}
                  </button>
                ) : (
                  <NavLink
                    to={workspace.path}
                    onClick={onNavigate}
                    className={({ isActive: linkActive }) => `
                      flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150
                      ${linkActive || isActive
                        ? 'bg-[#0D1B33] text-white' 
                        : 'text-[#334155] hover:bg-[#F1F5F9]'
                      }
                    `}
                    data-testid={`nav-${workspace.id}`}
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="text-sm font-medium">{workspace.name}</span>
                  </NavLink>
                )}
                
                {/* Sub-items (contextual navigation) */}
                {hasSubItems && isExpanded && (
                  <div className="mt-1 ml-4 pl-3 border-l border-[#E2E8F0] space-y-0.5">
                    {workspace.subItems.map((subItem) => {
                      const SubIcon = subItem.icon;
                      return (
                        <NavLink
                          key={subItem.path}
                          to={subItem.path}
                          onClick={onNavigate}
                          className={({ isActive: subActive }) => `
                            flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-all duration-150
                            ${subActive 
                              ? 'bg-[#F1F5F9] text-[#0D1B33] font-medium' 
                              : 'text-[#64748B] hover:bg-[#F8FAFC] hover:text-[#334155]'
                            }
                          `}
                          data-testid={`nav-sub-${subItem.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <SubIcon className="w-3.5 h-3.5 shrink-0 opacity-70" />
                          <span>{subItem.name}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </nav>

      {/* User Footer */}
      <div className="border-t border-[#E2E8F0] p-3">
        <NavLink 
          to="/settings/profile"
          className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-[#F1F5F9] transition-colors"
        >
          <div 
            className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0"
            style={{ backgroundColor: "#0D1B33", color: "#176DFF" }}
          >
            {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate text-[#0D1B33]">
              {user?.name || user?.email}
            </div>
            <div className="text-[11px] text-[#64748B] capitalize">
              {user?.role?.replace(/_/g, " ")}
            </div>
          </div>
        </NavLink>
      </div>
    </aside>
  );
}
